"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MultiStatusResponse = exports.ActionDestinationErrorResponse = exports.ActionDestinationSuccessResponse = exports.Action = exports.hookTypeStrings = void 0;
const events_1 = require("events");
const create_request_client_1 = __importDefault(require("../create-request-client"));
const mapping_kit_1 = require("../mapping-kit");
const fields_to_jsonschema_1 = require("./fields-to-jsonschema");
const fetch_1 = require("../fetch");
const types_1 = require("./types");
const request_client_1 = require("../request-client");
const schema_validation_1 = require("../schema-validation");
const errors_1 = require("../errors");
const remove_empty_values_1 = require("../remove-empty-values");
const get_1 = require("../get");
exports.hookTypeStrings = ['onMappingSave', 'retlOnMappingSave'];
const isSyncMode = (value) => {
    return types_1.syncModeTypes.find((validValue) => value === validValue) !== undefined;
};
class Action extends events_1.EventEmitter {
    constructor(destinationName, definition, extendRequest) {
        super();
        this.definition = definition;
        this.destinationName = destinationName;
        this.extendRequest = extendRequest;
        this.hasBatchSupport = typeof definition.performBatch === 'function';
        this.hasHookSupport = definition.hooks !== undefined;
        if (Object.keys(definition.fields ?? {}).length) {
            this.schema = (0, fields_to_jsonschema_1.fieldsToJsonSchema)(definition.fields);
        }
        if (definition.hooks) {
            for (const hookName in definition.hooks) {
                const hook = definition.hooks[hookName];
                if (hook?.inputFields) {
                    if (!this.hookSchemas) {
                        this.hookSchemas = {};
                    }
                    const castedInputFields = {};
                    for (const key in hook.inputFields) {
                        const field = hook.inputFields[key];
                        if (field.dynamic) {
                            castedInputFields[key] = {
                                ...field,
                                dynamic: true
                            };
                        }
                        else {
                            castedInputFields[key] = {
                                ...field,
                                dynamic: false
                            };
                        }
                    }
                    this.hookSchemas[hookName] = (0, fields_to_jsonschema_1.fieldsToJsonSchema)(castedInputFields);
                }
            }
        }
    }
    async execute(bundle) {
        const results = [];
        let payload = (0, mapping_kit_1.transform)(bundle.mapping, bundle.data);
        results.push({ output: 'Mappings resolved' });
        payload = (0, remove_empty_values_1.removeEmptyValues)(payload, this.schema, true);
        if (this.schema) {
            const schemaKey = `${this.destinationName}:${this.definition.title}`;
            (0, schema_validation_1.validateSchema)(payload, this.schema, {
                schemaKey,
                statsContext: bundle.statsContext,
                exempt: ['dynamicAuthSettings']
            });
            results.push({ output: 'Payload validated' });
        }
        let hookOutputs = {};
        if (this.definition.hooks) {
            for (const hookType in this.definition.hooks) {
                const hookOutputValues = bundle.mapping?.[hookType];
                if (hookOutputValues) {
                    hookOutputs = { ...hookOutputs, [hookType]: hookOutputValues };
                }
            }
        }
        const syncMode = this.definition.syncMode ? bundle.mapping?.['__segment_internal_sync_mode'] : undefined;
        const matchingKey = bundle.mapping?.['__segment_internal_matching_key'];
        const dataBundle = {
            rawData: bundle.data,
            rawMapping: bundle.mapping,
            settings: bundle.settings,
            payload,
            auth: bundle.auth,
            features: bundle.features,
            statsContext: bundle.statsContext,
            logger: bundle.logger,
            engageDestinationCache: bundle.engageDestinationCache,
            transactionContext: bundle.transactionContext,
            stateContext: bundle.stateContext,
            audienceSettings: bundle.audienceSettings,
            hookOutputs,
            syncMode: isSyncMode(syncMode) ? syncMode : undefined,
            matchingKey: matchingKey ? String(matchingKey) : undefined,
            subscriptionMetadata: bundle.subscriptionMetadata
        };
        const output = await this.performRequest(this.definition.perform, dataBundle);
        results.push({ data: output, output: 'Action Executed' });
        return results;
    }
    async executeBatch(bundle) {
        if (!this.hasBatchSupport) {
            throw new errors_1.IntegrationError('This action does not support batched requests.', 'NotImplemented', 501);
        }
        const mapping = bundle.mapping;
        let payloads = (0, mapping_kit_1.transformBatch)(mapping, bundle.data);
        const batchPayloadLength = payloads.length;
        const multiStatusResponse = [];
        const invalidPayloadIndices = new Set();
        if (this.schema) {
            const schema = this.schema;
            const validationOptions = {
                schemaKey: `${this.destinationName}:${this.definition.title}`,
                throwIfInvalid: true,
                statsContext: bundle.statsContext,
                exempt: ['dynamicAuthSettings']
            };
            {
                const filteredPayload = [];
                for (let i = 0; i < payloads.length; i++) {
                    const payload = (0, remove_empty_values_1.removeEmptyValues)(payloads[i], schema);
                    try {
                        (0, schema_validation_1.validateSchema)(payload, schema, validationOptions);
                    }
                    catch (e) {
                        multiStatusResponse[i] = {
                            status: 400,
                            errortype: errors_1.ErrorCodes.PAYLOAD_VALIDATION_FAILED,
                            errormessage: e.message,
                            errorreporter: errors_1.MultiStatusErrorReporter.INTEGRATIONS
                        };
                        invalidPayloadIndices.add(i);
                        bundle.statsContext?.statsClient?.incr('action.multistatus_discard', 1, bundle.statsContext?.tags);
                        continue;
                    }
                    filteredPayload.push(payload);
                }
                payloads = filteredPayload;
            }
        }
        let hookOutputs = {};
        if (this.definition.hooks) {
            for (const hookType in this.definition.hooks) {
                const hookOutputValues = bundle.mapping?.[hookType];
                if (hookOutputValues) {
                    hookOutputs = { ...hookOutputs, [hookType]: hookOutputValues };
                }
            }
        }
        if (payloads.length === 0) {
            return multiStatusResponse;
        }
        if (this.definition.performBatch) {
            const syncMode = this.definition.syncMode ? bundle.mapping?.['__segment_internal_sync_mode'] : undefined;
            const matchingKey = bundle.mapping?.['__segment_internal_matching_key'];
            const data = {
                rawData: bundle.data,
                rawMapping: bundle.mapping,
                settings: bundle.settings,
                audienceSettings: bundle.audienceSettings,
                payload: payloads,
                auth: bundle.auth,
                features: bundle.features,
                statsContext: bundle.statsContext,
                logger: bundle.logger,
                engageDestinationCache: bundle.engageDestinationCache,
                transactionContext: bundle.transactionContext,
                stateContext: bundle.stateContext,
                subscriptionMetadata: bundle.subscriptionMetadata,
                hookOutputs,
                syncMode: isSyncMode(syncMode) ? syncMode : undefined,
                matchingKey: matchingKey ? String(matchingKey) : undefined
            };
            const requestClient = this.createRequestClient(data);
            const performBatchResponse = await this.definition.performBatch(requestClient, data);
            if (performBatchResponse instanceof fetch_1.Response) {
                let parsedBody = {};
                parsedBody =
                    performBatchResponse?.data ??
                        performBatchResponse?.content ??
                        {};
                this.fillMultiStatusResponse({
                    multiStatusResponse,
                    invalidPayloadIndices,
                    batchPayloadLength,
                    status: performBatchResponse.status,
                    body: parsedBody,
                    filteredPayloads: payloads
                });
                return multiStatusResponse;
            }
            if (performBatchResponse instanceof request_client_1.HTTPError) {
                this.fillMultiStatusResponse({
                    multiStatusResponse,
                    invalidPayloadIndices,
                    batchPayloadLength,
                    status: performBatchResponse.response.status,
                    body: performBatchResponse.message,
                    filteredPayloads: payloads
                });
                return multiStatusResponse;
            }
            if (performBatchResponse instanceof MultiStatusResponse) {
                let resultsReadIndex = 0;
                for (let i = 0; i < batchPayloadLength; i++) {
                    if (invalidPayloadIndices.has(i)) {
                        continue;
                    }
                    const response = performBatchResponse.getResponseAtIndex(resultsReadIndex++);
                    if (!response) {
                        multiStatusResponse[i] = {
                            status: 500,
                            errormessage: 'MultiStatusResponse is missing a response at the specified index',
                            errortype: errors_1.ErrorCodes.PAYLOAD_VALIDATION_FAILED,
                            errorreporter: errors_1.MultiStatusErrorReporter.INTEGRATIONS
                        };
                        bundle.statsContext?.statsClient?.incr('action.multistatus_discard', 1, bundle.statsContext?.tags);
                        continue;
                    }
                    if (response instanceof ActionDestinationErrorResponse) {
                        const responseValue = response.value();
                        multiStatusResponse[i] = {
                            ...responseValue,
                            errorreporter: responseValue.sent || responseValue.body
                                ? errors_1.MultiStatusErrorReporter.DESTINATION
                                : errors_1.MultiStatusErrorReporter.INTEGRATIONS
                        };
                        bundle.statsContext?.statsClient?.incr('destination.multistatus_discard', 1, bundle.statsContext?.tags);
                        continue;
                    }
                    multiStatusResponse[i] = response.value();
                }
                return multiStatusResponse;
            }
            this.fillMultiStatusResponse({
                multiStatusResponse,
                invalidPayloadIndices,
                batchPayloadLength,
                status: 200,
                body: {},
                filteredPayloads: payloads
            });
        }
        return multiStatusResponse;
    }
    extractFieldContextAndHandler(field) {
        const arrayRegex = /(.*)\.\[(\d+)\]\.(.*)/;
        const objectRegex = /(.*)\.(.*)/;
        let dynamicHandlerPath = field;
        let dynamicFieldContext;
        const match = arrayRegex.exec(field) || objectRegex.exec(field);
        if (match) {
            const [, parent, indexOrChild, child] = match;
            if (child) {
                dynamicFieldContext = { selectedArrayIndex: parseInt(indexOrChild, 10) };
                dynamicHandlerPath = `${parent}.${child}`;
            }
            else {
                const parentFetcher = this.definition.dynamicFields?.[parent];
                if (parentFetcher && !(indexOrChild in parentFetcher)) {
                    dynamicHandlerPath = `${parent}.__values__`;
                    dynamicFieldContext = { selectedKey: indexOrChild };
                }
            }
        }
        return { dynamicHandlerPath, dynamicFieldContext };
    }
    async executeDynamicField(field, data, dynamicFn) {
        if (dynamicFn && typeof dynamicFn === 'function') {
            return (await this.performRequest(dynamicFn, { ...data }));
        }
        const { dynamicHandlerPath, dynamicFieldContext } = this.extractFieldContextAndHandler(field);
        const fn = (0, get_1.get)(this.definition.dynamicFields, dynamicHandlerPath);
        if (typeof fn !== 'function') {
            return Promise.resolve({
                choices: [],
                nextPage: '',
                error: {
                    message: `No dynamic field named ${field} found.`,
                    code: '404'
                }
            });
        }
        return (await this.performRequest(fn, { ...data, dynamicFieldContext }));
    }
    async executeHook(hookType, data) {
        if (!this.hasHookSupport) {
            throw new errors_1.IntegrationError('This action does not support any hooks.', 'NotImplemented', 501);
        }
        const hookFn = this.definition.hooks?.[hookType]?.performHook;
        if (!hookFn) {
            throw new errors_1.IntegrationError(`Missing implementation for hook: ${hookType}.`, 'NotImplemented', 501);
        }
        if (this.hookSchemas?.[hookType]) {
            const schema = this.hookSchemas[hookType];
            (0, schema_validation_1.validateSchema)(data.hookInputs, schema, {
                exempt: ['dynamicAuthSettings']
            });
        }
        return (await this.performRequest(hookFn, data));
    }
    async performRequest(requestFn, data) {
        const requestClient = this.createRequestClient(data);
        const response = await requestFn(requestClient, data);
        return this.parseResponse(response);
    }
    createRequestClient(data) {
        const options = this.extendRequest?.(data) ?? {};
        return (0, create_request_client_1.default)(options, {
            afterResponse: [this.afterResponse.bind(this)],
            statsContext: data.statsContext
        });
    }
    afterResponse(request, options, response) {
        const modifiedResponse = response;
        modifiedResponse.request = request;
        modifiedResponse.options = options;
        this.emit('response', modifiedResponse);
        return modifiedResponse;
    }
    parseResponse(response) {
        if (response instanceof fetch_1.Response) {
            return response.data ?? response.content;
        }
        return response;
    }
    fillMultiStatusResponse(input) {
        const { multiStatusResponse, batchPayloadLength, status, body, filteredPayloads } = input;
        let payloadReadIndex = 0;
        for (let i = 0; i < batchPayloadLength; i++) {
            if (input.invalidPayloadIndices.has(i)) {
                continue;
            }
            multiStatusResponse[i] = {
                status: status,
                body: body,
                sent: filteredPayloads ? filteredPayloads[payloadReadIndex++] : {}
            };
        }
    }
}
exports.Action = Action;
class ActionDestinationSuccessResponse {
    constructor(data) {
        this.data = data;
    }
    value() {
        return this.data;
    }
}
exports.ActionDestinationSuccessResponse = ActionDestinationSuccessResponse;
class ActionDestinationErrorResponse {
    constructor(data) {
        this.data = data;
        if (!this.data.errortype) {
            this.data.errortype = (0, errors_1.getErrorCodeFromHttpStatus)(this.data.status);
        }
    }
    value() {
        return this.data;
    }
}
exports.ActionDestinationErrorResponse = ActionDestinationErrorResponse;
class MultiStatusResponse {
    constructor() {
        this.responses = [];
    }
    length() {
        return this.responses.length;
    }
    pushResponseObject(response) {
        this.responses.push(response);
    }
    pushSuccessResponse(response) {
        if (response instanceof ActionDestinationSuccessResponse) {
            this.responses.push(response);
        }
        else {
            this.responses.push(new ActionDestinationSuccessResponse(response));
        }
    }
    pushErrorResponse(response) {
        if (response instanceof ActionDestinationErrorResponse) {
            this.responses.push(response);
        }
        else {
            this.responses.push(new ActionDestinationErrorResponse(response));
        }
    }
    pushResponseObjectAtIndex(index, response) {
        this.responses[index] = response;
    }
    setSuccessResponseAtIndex(index, response) {
        if (response instanceof ActionDestinationSuccessResponse) {
            this.responses[index] = response;
        }
        else {
            this.responses[index] = new ActionDestinationSuccessResponse(response);
        }
    }
    setErrorResponseAtIndex(index, response) {
        if (response instanceof ActionDestinationErrorResponse) {
            this.responses[index] = response;
        }
        else {
            this.responses[index] = new ActionDestinationErrorResponse(response);
        }
    }
    unsetResponseAtIndex(index) {
        delete this.responses[index];
    }
    isSuccessResponseAtIndex(index) {
        return this.responses[index] instanceof ActionDestinationSuccessResponse;
    }
    isErrorResponseAtIndex(index) {
        return this.responses[index] instanceof ActionDestinationErrorResponse;
    }
    getResponseAtIndex(index) {
        return this.responses[index];
    }
    getAllResponses() {
        return this.responses;
    }
}
exports.MultiStatusResponse = MultiStatusResponse;
//# sourceMappingURL=action.js.map